/**
 * QueuesPage — the Work Queue (PAC-008/009), wireframe 1.
 *
 * The advocate's role-first landing page: a greeting, an SLA-ranked work queue
 * (GET /tasks, breached first) on the primary surface, and a right-context
 * "Today's summary" panel plus an advisory AI insight card. Each row opens the
 * underlying case.
 *
 * This phase adds:
 *   - A **My Queue / Flagged Issues** tab toggle. "Flagged" is a client-side
 *     filter of the same task data — cases with a breached SLA or a flag.
 *   - A per-row **talking-points** preview popover (derived/mock from the task),
 *     so the advocate can prep before opening the case.
 */
import { useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '../hooks/useTasks';
import { useAuth } from '../auth/useAuth';
import type { Task } from '../api/types';
import { colors, tints, radius, shadow } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { ThreePanel } from '../components/ThreePanel';
import { Card } from '../components/Card';
import { Toolbar } from '../components/Toolbar';
import { DataTable, type Column } from '../components/DataTable';
import { PriorityPill, tierForScore } from '../components/PriorityPill';
import { SlaCountdown, classifySla } from '../components/SlaCountdown';
import { StatusBadge } from '../components/StatusBadge';
import { RoleBadge } from '../components/RoleBadge';
import { AIInsightCard } from '../components/AIInsightCard';
import { RoleImpactStrip } from '../components/PlatformImpactStrip';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { hospitalLabel } from '../components/nav';
import { useQueueRanking } from '../hooks/useIntelligence';

const QUEUE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All queues' },
  { value: 'pas', label: 'PAS' },
  { value: 'bas', label: 'BAS' },
  { value: 'state_ops', label: 'State Ops' },
  { value: 'finance', label: 'Finance' },
  { value: 'retention', label: 'Retention' },
  { value: 'case_management', label: 'Case Management' },
];

type Tab = 'mine' | 'flagged';

/** A task is "flagged" when its SLA is breached or its status reads as flagged. */
function isFlagged(t: Task): boolean {
  const sla = classifySla(t.due_at);
  if (sla.label.startsWith('Breached')) return true;
  const s = t.status.toLowerCase();
  return s === 'flagged' || s === 'escalated';
}

/** Derive a short, mock set of talking points for a task (preview popover). */
function talkingPoints(t: Task): string[] {
  const points: string[] = [];
  const stage = t.state.replace(/_/g, ' ');
  points.push(`Confirm identity and consent before discussing ${stage}.`);
  if (tierForScore(t.priority_score) === 'critical') {
    points.push('High-priority case — lead with the time-sensitive coverage window.');
  }
  const sla = classifySla(t.due_at);
  if (sla.label.startsWith('Breached')) {
    points.push('SLA already breached — apologize for the delay and offer to resolve today.');
  } else if (sla.label.includes('h')) {
    points.push('SLA closes soon — aim to complete this step on the call.');
  }
  if (!t.owner_id) {
    points.push('Unassigned — claim the case if you take the call.');
  }
  points.push(`Next step: advance the ${stage} task and log the outcome.`);
  return points;
}

export function QueuesPage() {
  const { user, tenantId } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState('');
  const [tab, setTab] = useState<Tab>('mine');
  const [openPoints, setOpenPoints] = useState<string | null>(null);
  const [aiRank, setAiRank] = useState(false);
  const { tasks, isLoading, error, refresh } = useTasks(queue || undefined);
  const ranking = useQueueRanking(queue || undefined, aiRank);

  const flaggedCount = useMemo(() => tasks.filter(isFlagged).length, [tasks]);

  const baseTasks = useMemo(
    () => (tab === 'flagged' ? tasks.filter(isFlagged) : tasks),
    [tasks, tab]
  );

  /**
   * Map each task to a model rank vs its current (rules/SLA) rank. Prefers the
   * live /ai/queue-ranking overlay; otherwise derives a model rank from the
   * priority score so the model-vs-rules comparison is always demonstrable.
   */
  const rankInfo = useMemo(() => {
    const rulesOrder = baseTasks.map((t) => t.id); // current display order = rules/SLA rank
    const rulesRank = new Map(rulesOrder.map((id, i) => [id, i + 1]));
    const modelOrder = [...baseTasks]
      .map((t) => ({ id: t.id, score: ranking.byCase[t.case_id]?.model_score ?? t.priority_score }))
      .sort((a, b) => b.score - a.score)
      .map((x) => x.id);
    const modelRank = new Map(modelOrder.map((id, i) => [id, i + 1]));
    return { rulesRank, modelRank };
  }, [baseTasks, ranking.byCase]);

  // When AI ranking is on, reorder rows by the model rank.
  const visibleTasks = useMemo(() => {
    if (!aiRank) return baseTasks;
    return [...baseTasks].sort(
      (a, b) => (rankInfo.modelRank.get(a.id) ?? 0) - (rankInfo.modelRank.get(b.id) ?? 0)
    );
  }, [aiRank, baseTasks, rankInfo]);

  const summary = useMemo(() => {
    let critical = 0;
    let breached = 0;
    let dueSoon = 0;
    let unassigned = 0;
    for (const t of tasks) {
      if (tierForScore(t.priority_score) === 'critical') critical += 1;
      const sla = classifySla(t.due_at);
      if (sla.label.startsWith('Breached')) breached += 1;
      else if (sla.label.includes('h')) dueSoon += 1;
      if (!t.owner_id) unassigned += 1;
    }
    return { total: tasks.length, critical, breached, dueSoon, unassigned };
  }, [tasks]);

  const rankColumn: Column<Task> = {
    key: 'airank',
    header: 'AI rank',
    width: 96,
    render: (t) => {
      const m = rankInfo.modelRank.get(t.id) ?? 0;
      const r = rankInfo.rulesRank.get(t.id) ?? 0;
      const moved = r - m; // positive = model promotes this case
      return (
        <span style={rankCell} title={`Model #${m} · Rules #${r}`}>
          <span style={rankModel}>#{m}</span>
          {moved !== 0 && (
            <span style={{ ...rankDelta, color: moved > 0 ? colors.success : colors.danger }}>
              {moved > 0 ? '▲' : '▼'}
              {Math.abs(moved)}
            </span>
          )}
        </span>
      );
    },
  };

  const columns: Column<Task>[] = [
    ...(aiRank ? [rankColumn] : []),
    {
      key: 'priority',
      header: 'Priority',
      width: 110,
      render: (t) => <PriorityPill tier={tierForScore(t.priority_score)} />,
    },
    {
      key: 'case',
      header: 'Case',
      render: (t) => <span style={caseLink}>{t.case_id}</span>,
    },
    { key: 'queue', header: 'Queue', render: (t) => <RoleBadge role={t.queue_role} /> },
    {
      key: 'stage',
      header: 'Stage',
      render: (t) => <span style={cellMuted}>{t.state.replace(/_/g, ' ')}</span>,
    },
    { key: 'status', header: 'Status', render: (t) => <StatusBadge value={t.status} /> },
    { key: 'sla', header: 'SLA', render: (t) => <SlaCountdown dueAt={t.due_at} /> },
    {
      key: 'owner',
      header: 'Assignee',
      render: (t) => t.owner_id ?? <span style={cellMuted}>Unassigned</span>,
    },
    {
      key: 'prep',
      header: 'Prep',
      width: 80,
      render: (t) => (
        <div
          style={{ position: 'relative' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            style={prepBtn}
            aria-label={`Talking points for ${t.case_id}`}
            onClick={() => setOpenPoints((cur) => (cur === t.id ? null : t.id))}
          >
            Talking points
          </button>
          {openPoints === t.id && (
            <div style={popover} role="dialog">
              <div style={popoverHead}>
                <span style={popoverTitle}>Talking points</span>
                <button type="button" style={popoverClose} onClick={() => setOpenPoints(null)} aria-label="Close">
                  ×
                </button>
              </div>
              <ul style={popoverList}>
                {talkingPoints(t).map((p, i) => (
                  <li key={i} style={popoverItem}>
                    {p}
                  </li>
                ))}
              </ul>
              <div style={popoverFooter}>Advisory prep · review before the call.</div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'open',
      header: '',
      align: 'right',
      render: () => <span style={openBtn}>Open →</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title="Work Queue"
        greetingName={user?.name?.split(' ')[0] ?? 'there'}
        greetingTail={
          summary.total > 0
            ? `You have ${summary.total} task${summary.total === 1 ? '' : 's'} waiting.`
            : 'Your queue is clear.'
        }
        subtitle="SLA-ranked across the enrollment lifecycle — breached SLAs and critical-tier cases surface first."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      <RoleImpactStrip />

      <ThreePanel
        primary={
          <Card
            title={
              <div style={tabRow} role="tablist" aria-label="Work queue view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'mine'}
                  style={tab === 'mine' ? tabActive : tabBtn}
                  onClick={() => setTab('mine')}
                >
                  My Queue
                  <span style={tab === 'mine' ? tabCountActive : tabCount}>{summary.total}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'flagged'}
                  style={tab === 'flagged' ? tabActive : tabBtn}
                  onClick={() => setTab('flagged')}
                >
                  Flagged Issues
                  <span style={tab === 'flagged' ? tabCountActive : flaggedBadge}>{flaggedCount}</span>
                </button>
              </div>
            }
            actions={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setAiRank((v) => !v)}
                  aria-pressed={aiRank}
                  style={{ ...aiToggle, ...(aiRank ? aiToggleOn : {}) }}
                  title="Re-rank the queue by the model's conversion-propensity score (advisory)"
                >
                  ✦ AI ranking{aiRank ? ' on' : ''}
                  {aiRank && ranking.available && <span style={liveDot} aria-label="live" />}
                </button>
                <select
                  value={queue}
                  onChange={(e) => setQueue(e.target.value)}
                  aria-label="Queue filter"
                  style={select}
                >
                  {QUEUE_FILTERS.map((q) => (
                    <option key={q.value} value={q.value}>
                      {q.label}
                    </option>
                  ))}
                </select>
              </div>
            }
            padded={false}
          >
            {isLoading && (
              <div style={{ padding: 18 }}>
                <LoadingIndicator text="Loading tasks…" />
              </div>
            )}
            {error && (
              <p style={errorText} role="alert">
                {error.message}
              </p>
            )}
            {!isLoading && !error && (
              <DataTable
                columns={columns}
                rows={visibleTasks}
                rowKey={(t) => t.id}
                onRowClick={(t) => navigate(`/cases/${t.case_id}`)}
                emptyTitle={tab === 'flagged' ? 'No flagged issues' : 'No tasks in this queue'}
                emptyDescription={
                  tab === 'flagged'
                    ? 'No breached SLAs or flagged cases right now.'
                    : 'Nothing is waiting on this role right now.'
                }
              />
            )}
          </Card>
        }
        rightContext={
          <>
            <SummaryPanel
              hospital={hospitalLabel(tenantId)}
              total={summary.total}
              critical={summary.critical}
              breached={summary.breached}
              dueSoon={summary.dueSoon}
              unassigned={summary.unassigned}
            />
            <AIInsightCard confidence={0.86} heading="Queue focus" acceptLabel="Prioritize">
              {summary.breached > 0
                ? `${summary.breached} case${summary.breached === 1 ? '' : 's'} ${summary.breached === 1 ? 'has' : 'have'} a breached SLA. Recommend handling these before new outreach to protect conversion.`
                : 'No breached SLAs. Recommend starting with critical-tier cases by conversion probability.'}
            </AIInsightCard>
            <Toolbar style={{ justifyContent: 'flex-start' }}>
              <span style={hintText}>Tip: switch hospital or role in the top bar to re-scope this queue.</span>
            </Toolbar>
          </>
        }
      />
    </>
  );
}

function SummaryPanel({
  hospital,
  total,
  critical,
  breached,
  dueSoon,
  unassigned,
}: {
  hospital: string;
  total: number;
  critical: number;
  breached: number;
  dueSoon: number;
  unassigned: number;
}) {
  const rows: { label: string; value: number; color: string }[] = [
    { label: 'Open tasks', value: total, color: colors.textPrimary },
    { label: 'Critical tier', value: critical, color: colors.priorityCritical },
    { label: 'SLA breached', value: breached, color: colors.danger },
    { label: 'Due within 24h', value: dueSoon, color: colors.warning },
    { label: 'Unassigned', value: unassigned, color: colors.textSecondary },
  ];
  return (
    <div>
      <div style={summaryTitle}>Today's summary</div>
      <div style={summaryHospital}>{hospital}</div>
      <dl style={summaryGrid}>
        {rows.map((r) => (
          <div key={r.label} style={summaryRow}>
            <dt style={summaryLabel}>{r.label}</dt>
            <dd style={{ ...summaryValue, color: r.color }}>{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const select: CSSProperties = {
  padding: '5px 10px',
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  fontSize: '0.86rem',
  background: '#FFFFFF',
  fontFamily: 'inherit',
};
const caseLink: CSSProperties = { color: colors.action, fontWeight: 600 };
const aiToggle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.pill,
  background: colors.white,
  color: colors.textSecondary,
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const aiToggleOn: CSSProperties = {
  background: tints.action,
  borderColor: colors.action,
  color: colors.action,
};
const liveDot: CSSProperties = { width: 7, height: 7, borderRadius: '50%', background: colors.success };
const rankCell: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6 };
const rankModel: CSSProperties = { fontWeight: 700, color: colors.action };
const rankDelta: CSSProperties = { fontSize: '0.72rem', fontWeight: 700 };
const cellMuted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const openBtn: CSSProperties = { color: colors.action, fontWeight: 600, fontSize: '0.84rem' };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', padding: 18 };
const hintText: CSSProperties = { fontSize: '0.78rem', color: colors.textSecondary, lineHeight: 1.5 };

const tabRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 4 };
const tabBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 12px',
  border: 'none',
  background: 'none',
  borderRadius: radius.sm,
  fontSize: '0.92rem',
  fontWeight: 600,
  color: colors.textSecondary,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const tabActive: CSSProperties = {
  ...tabBtn,
  background: tints.action,
  color: colors.action,
};
const tabCount: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  padding: '1px 7px',
  borderRadius: 999,
  background: colors.surfaceAlt,
  color: colors.textSecondary,
};
const tabCountActive: CSSProperties = {
  ...tabCount,
  background: colors.action,
  color: '#FFFFFF',
};
const flaggedBadge: CSSProperties = {
  ...tabCount,
  background: tints.danger,
  color: tints.dangerText,
};

const prepBtn: CSSProperties = {
  padding: '3px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: '#FFFFFF',
  fontSize: '0.74rem',
  fontWeight: 600,
  color: colors.action,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};
const popover: CSSProperties = {
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: 6,
  width: 300,
  background: '#FFFFFF',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  boxShadow: shadow.lg,
  padding: '12px 14px',
  zIndex: 20,
  textAlign: 'left',
  whiteSpace: 'normal',
  cursor: 'default',
};
const popoverHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
};
const popoverTitle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
};
const popoverClose: CSSProperties = {
  border: 'none',
  background: 'none',
  fontSize: '1.1rem',
  lineHeight: 1,
  color: colors.textSecondary,
  cursor: 'pointer',
  padding: 0,
};
const popoverList: CSSProperties = { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 };
const popoverItem: CSSProperties = { fontSize: '0.82rem', color: colors.textPrimary, lineHeight: 1.45 };
const popoverFooter: CSSProperties = { marginTop: 10, fontSize: '0.72rem', color: colors.textMuted };

const summaryTitle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
};
const summaryHospital: CSSProperties = { fontSize: '0.86rem', fontWeight: 600, color: colors.textPrimary, marginTop: 2, marginBottom: 10 };
const summaryGrid: CSSProperties = { margin: 0, display: 'flex', flexDirection: 'column', gap: 0 };
const summaryRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 0',
  borderBottom: `1px solid ${colors.surfaceAlt}`,
};
const summaryLabel: CSSProperties = { fontSize: '0.86rem', color: colors.textSecondary };
const summaryValue: CSSProperties = { margin: 0, fontSize: '1.15rem', fontWeight: 700 };
